import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, ActivityIndicator } from 'react-native';
import { SaveIcon, SettingsIcon, CloseIcon, WarningIcon, CheckCircleIcon, ErrorIcon, EyeIcon, EyeOffIcon } from '../icons';
import { styles, colors } from '../styles';
import { saveConfig, checkConfig } from '../services';

const ConfigForm = ({ userId, systemKey, onSave, onCancel }) => {
  const [ramal, setRamal] = useState('');
  const [usuario, setUsuario] = useState('');
  const [token, setToken] = useState('');
  const [cliente_id, setCliente_id] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [notification, setNotification] = useState(null);
  const [ramalError, setRamalError] = useState(null);
  const [usuarioError, setUsuarioError] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [cliente_idError, setCliente_idError] = useState(null);
  const [ramalFocused, setRamalFocused] = useState(false);
  const [usuarioFocused, setUsuarioFocused] = useState(false);
  const [tokenFocused, setTokenFocused] = useState(false);
  const [cliente_idFocused, setCliente_idFocused] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [saveButtonHover, setSaveButtonHover] = useState(false);
  const [cancelButtonHover, setCancelButtonHover] = useState(false);
  const [confirmButtonHover, setConfirmButtonHover] = useState(false);
  const [cancelConfirmButtonHover, setCancelConfirmButtonHover] = useState(false);
  
  // Refs para prevenir requisições duplicadas
  const isCheckingConfigRef = useRef(false);
  const isSavingConfigRef = useRef(false);

  // Carregar configuração ao montar o componente
  useEffect(() => {
    if (userId && !isCheckingConfigRef.current) {
      isCheckingConfigRef.current = true;
      checkConfig(userId)
        .then((config) => {
          if (config.ramal) {
            setRamal(config.ramal);
          }
          if (config.usuario) {
            setUsuario(config.usuario);
          }
          if (config.token) {
            setToken(config.token);
          }
          if (config.cliente_id) {
            setCliente_id(config.cliente_id.toString());
          }
        })
        .catch((err) => {
          console.error('Erro ao carregar configuração:', err);
        })
        .finally(() => {
          isCheckingConfigRef.current = false;
        });
    }
  }, [userId]);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 10000);
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const handleSave = () => {
    // Limpar erros anteriores
    setRamalError(null);
    setUsuarioError(null);
    setTokenError(null);
    setCliente_idError(null);
    setNotification(null);

    // Validar campos
    let hasError = false;
    if (!ramal || ramal.trim() === '') {
      setRamalError('Ramal é obrigatório');
      hasError = true;
    }
    if (!usuario || usuario.trim() === '') {
      setUsuarioError('Usuário é obrigatório');
      hasError = true;
    }
    if (!token || token.trim() === '') {
      setTokenError('Token é obrigatório');
      hasError = true;
    }
    if (!cliente_id || cliente_id.trim() === '') {
      setCliente_idError('Cliente ID é obrigatório');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (!userId) {
      showNotification('Aguardando sistema...', 'error');
      return;
    }

    // Mostrar confirmação
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    // Prevenir requisições duplicadas
    if (isSavingConfigRef.current || loading) {
      return;
    }
    
    isSavingConfigRef.current = true;
    setLoading(true);
    setShowConfirmation(false);
    setNotification(null);

    try {
      const clienteIdNum = parseInt(cliente_id, 10);
      if (isNaN(clienteIdNum)) {
        throw new Error('Cliente ID deve ser um número válido');
      }
      
      const result = await saveConfig(userId, systemKey, ramal, usuario, token, clienteIdNum);
      
      // Sucesso - mostrar notificação de sucesso e chamar onSave
      showNotification('Configuração salva com sucesso!', 'success');
      setTimeout(() => {
        setShowConfirmation(false);
        onSave();
      }, 1000);
    } catch (err) {
      // Erro retornado pela API
      setShowConfirmation(false);
      
      // Determinar mensagem baseada no status code
      let errorMessage = 'Erro ao salvar. Tente novamente.';
      
      if (err.status === 400) {
        errorMessage = 'Dados inválidos! Verifique e tente novamente.';
      } else if (err.status === 401) {
        errorMessage = 'O ramal, usuário ou token do Siptek estão inválidos.';
      } else if (err.data && err.data.error) {
        errorMessage = err.data.error;
      } else if (err.data && err.data.message) {
        errorMessage = err.data.message;
      } else if (err.message && !err.message.includes('Erro ao salvar configuração:')) {
        errorMessage = err.message;
      }
      
      // Mostrar notificação de erro
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
      isSavingConfigRef.current = false;
    }
  };

  const handleCancelConfirmation = () => {
    // Voltar ao estado inicial
    setShowConfirmation(false);
  };

  return (
    <View style={[styles.container, { padding: 20, justifyContent: 'flex-start', paddingTop: 40 }]}>
      {/* Notificação */}
      {notification && (
        <View style={{
          marginBottom: 20,
          padding: 18,
          borderRadius: 8,
          backgroundColor: notification.type === 'success' ? '#D1FAE5' : '#FEE2E2',
          borderWidth: 1,
          borderColor: notification.type === 'success' ? colors.success : colors.error,
          position: 'relative',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 3,
        }}>
          <TouchableOpacity
            onPress={closeNotification}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              padding: 4,
              zIndex: 1,
              borderRadius: 4,
            }}
            activeOpacity={0.7}
          >
            <CloseIcon 
              size={18} 
              color={notification.type === 'success' ? colors.success : colors.error} 
            />
          </TouchableOpacity>
          
          <View style={{ 
            flexDirection: 'row', 
            alignItems: 'flex-start',
            paddingRight: 30,
          }}>
            <View style={{ 
              marginRight: 12, 
              marginTop: 2,
              width: 24,
              height: 24,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {notification.type === 'success' ? (
                <CheckCircleIcon size={24} color={colors.success} />
              ) : (
                <ErrorIcon size={24} color={colors.error} />
              )}
            </View>
            
            <View style={{ flex: 1 }}>
              <Text style={{
                color: notification.type === 'success' ? colors.success : colors.error,
                fontSize: 14,
                lineHeight: 20,
                fontWeight: '500',
              }}>
                {notification.message}
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Ícone acima */}
      <View style={{ alignItems: 'center', marginBottom: 32 }}>
        <SettingsIcon size={64} color={colors.borderLight} />
      </View>

      <View style={{ marginBottom: 20 }}>
        <Text style={[styles.text, { marginBottom: 8, fontWeight: '500' }]}>Ramal</Text>
        <TextInput
          style={[
            styles.input,
            ramalFocused && { 
              borderColor: colors.textPrimary,
              borderWidth: 2,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 2,
            },
            ramalError && { 
              borderColor: colors.error,
              borderWidth: 2,
            },
            !ramalFocused && !ramalError && {
              borderColor: colors.borderLight,
            }
          ]}
          value={ramal}
          onChangeText={(text) => {
            setRamal(text);
            if (ramalError) setRamalError(null);
          }}
          onFocus={() => setRamalFocused(true)}
          onBlur={() => setRamalFocused(false)}
          placeholder="Digite o número do ramal"
          placeholderTextColor="#94A3B8"
          keyboardType="numeric"
          editable={!loading && !!userId}
          autoComplete="off"
        />
        {ramalError && (
          <Text style={[styles.errorText, { marginTop: 6 }]}>{ramalError}</Text>
        )}
      </View>

      <View style={{ marginBottom: 20 }}>
        <Text style={[styles.text, { marginBottom: 8, fontWeight: '500' }]}>Usuário</Text>
        <TextInput
          style={[
            styles.input,
            usuarioFocused && { 
              borderColor: colors.textPrimary,
              borderWidth: 2,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 2,
            },
            usuarioError && { 
              borderColor: colors.error,
              borderWidth: 2,
            },
            !usuarioFocused && !usuarioError && {
              borderColor: colors.borderLight,
            }
          ]}
          value={usuario}
          onChangeText={(text) => {
            setUsuario(text);
            if (usuarioError) setUsuarioError(null);
          }}
          onFocus={() => setUsuarioFocused(true)}
          onBlur={() => setUsuarioFocused(false)}
          placeholder="Digite o usuário"
          placeholderTextColor="#94A3B8"
          editable={!loading && !!userId}
          autoComplete="username"
        />
        {usuarioError && (
          <Text style={[styles.errorText, { marginTop: 6 }]}>{usuarioError}</Text>
        )}
      </View>

      <View style={{ marginBottom: 20 }}>
        <Text style={[styles.text, { marginBottom: 8, fontWeight: '500' }]}>Token</Text>
        <View style={{ position: 'relative' }}>
          <TextInput
            style={[
              styles.input,
              tokenFocused && { 
                borderColor: colors.textPrimary,
                borderWidth: 2,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 2,
              },
              tokenError && { 
                borderColor: colors.error,
                borderWidth: 2,
              },
              !tokenFocused && !tokenError && {
                borderColor: colors.borderLight,
              },
              { paddingRight: token && token.trim() !== '' ? 45 : 14 }
            ]}
            value={token}
            onChangeText={(text) => {
              setToken(text);
              if (tokenError) setTokenError(null);
            }}
            onFocus={() => setTokenFocused(true)}
            onBlur={() => setTokenFocused(false)}
            placeholder="Digite o token"
            placeholderTextColor="#94A3B8"
            secureTextEntry={!showToken}
            editable={!loading && !!userId}
            autoComplete="off"
            textContentType="none"
          />
          {token && token.trim() !== '' ? (
            <TouchableOpacity
              onPress={() => setShowToken(!showToken)}
              style={{
                position: 'absolute',
                right: 12,
                top: 14,
                padding: 4,
                borderRadius: 4,
              }}
              activeOpacity={0.7}
            >
              {showToken ? (
                <EyeOffIcon size={20} color="#94A3B8" />
              ) : (
                <EyeIcon size={20} color="#94A3B8" />
              )}
            </TouchableOpacity>
          ) : null}
        </View>
        {tokenError && (
          <Text style={[styles.errorText, { marginTop: 6 }]}>{tokenError}</Text>
        )}
      </View>

      <View style={{ marginBottom: 24 }}>
        <Text style={[styles.text, { marginBottom: 8, fontWeight: '500' }]}>Cliente ID</Text>
        <TextInput
          style={[
            styles.input,
            cliente_idFocused && { 
              borderColor: colors.textPrimary,
              borderWidth: 2,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 2,
            },
            cliente_idError && { 
              borderColor: colors.error,
              borderWidth: 2,
            },
            !cliente_idFocused && !cliente_idError && {
              borderColor: colors.borderLight,
            }
          ]}
          value={cliente_id}
          onChangeText={(text) => {
            setCliente_id(text);
            if (cliente_idError) setCliente_idError(null);
          }}
          onFocus={() => setCliente_idFocused(true)}
          onBlur={() => setCliente_idFocused(false)}
          placeholder="Digite o ID do cliente"
          placeholderTextColor="#94A3B8"
          keyboardType="numeric"
          editable={!loading && !!userId}
          autoComplete="off"
        />
        {cliente_idError && (
          <Text style={[styles.errorText, { marginTop: 6 }]}>{cliente_idError}</Text>
        )}
      </View>

      {!userId && (
        <Text style={[styles.textSmall, { marginBottom: 16, opacity: 0.7, textAlign: 'center' }]}>
          Aguardando sistema...
        </Text>
      )}

      {/* Botões */}
      {!showConfirmation ? (
        <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
          <TouchableOpacity
            style={[
              styles.button,
              saveButtonHover && {
                ...styles.buttonHover,
                opacity: 0.9,
              },
              loading && { opacity: 0.6 },
              { flex: 1 }
            ]}
            onPress={handleSave}
            onMouseEnter={() => setSaveButtonHover(true)}
            onMouseLeave={() => setSaveButtonHover(false)}
            disabled={loading}
            activeOpacity={1}
          >
            {loading ? (
              <ActivityIndicator size="small" color={colors.buttonText} />
            ) : (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <SaveIcon size={18} color={colors.buttonText} />
                <Text style={styles.buttonText}>Salvar</Text>
              </View>
            )}
          </TouchableOpacity>

          {onCancel && (
            <TouchableOpacity
              style={[
                styles.button,
                {
                  backgroundColor: '#DC2626',
                  flex: 1,
                },
                cancelButtonHover && {
                  backgroundColor: '#B91C1C',
                  opacity: 0.9,
                }
              ]}
              onPress={onCancel}
              onMouseEnter={() => setCancelButtonHover(true)}
              onMouseLeave={() => setCancelButtonHover(false)}
              disabled={loading}
              activeOpacity={1}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <CloseIcon size={18} color={colors.buttonText} />
                <Text style={styles.buttonText}>Cancelar</Text>
              </View>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <View style={{
          marginTop: 24,
          padding: 20,
          backgroundColor: '#FFF4E6',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#FFB84D',
        }}>
          {/* Ícone e Mensagem */}
          <View style={{ 
            flexDirection: 'row', 
            alignItems: 'flex-start',
            marginBottom: 20,
          }}>
            <View style={{ marginRight: 12, marginTop: 2 }}>
              <WarningIcon size={24} color="#F59E0B" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.text, { 
                fontWeight: '600',
                color: colors.textPrimary,
                marginBottom: 4,
              }]}>
                Nova conexão PABX!
              </Text>
              <Text style={[styles.textSmall, { 
                opacity: 0.8,
                lineHeight: 20,
              }]}>
                Ao confirmar, uma nova conexão com o PABX será gerada para este usuário. Deseja continuar?
              </Text>
            </View>
          </View>

          {/* Botões */}
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity
              style={[
                styles.button,
                confirmButtonHover && {
                  ...styles.buttonHover,
                  opacity: 0.9,
                },
                loading && { opacity: 0.6 },
                { flex: 1 }
              ]}
              onPress={handleConfirmSave}
              onMouseEnter={() => setConfirmButtonHover(true)}
              onMouseLeave={() => setConfirmButtonHover(false)}
              disabled={loading}
              activeOpacity={1}
            >
              {loading ? (
                <ActivityIndicator size="small" color={colors.buttonText} />
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <SaveIcon size={18} color={colors.buttonText} />
                  <Text style={styles.buttonText}>Confirmar</Text>
                </View>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                {
                  backgroundColor: '#DC2626',
                  flex: 1,
                },
                cancelConfirmButtonHover && {
                  backgroundColor: '#B91C1C',
                  opacity: 0.9,
                }
              ]}
              onPress={handleCancelConfirmation}
              onMouseEnter={() => setCancelConfirmButtonHover(true)}
              onMouseLeave={() => setCancelConfirmButtonHover(false)}
              disabled={loading}
              activeOpacity={1}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <CloseIcon size={18} color={colors.buttonText} />
                <Text style={styles.buttonText}>Cancelar</Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

export default ConfigForm;

